import React, { useState } from 'react';
import debounce from 'lodash.debounce';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Tooltip,
  Form,
  Radio,
  Space,
  Alert,
  Slider,
  Flex,
} from 'antd';
import styled from 'styled-components';
import { t } from '../../../utils/i18n';
import { useGlobals } from '../../../globals-context';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import {
  paymentErrors,
  useInitiatePayPalSetup,
  useInitiateShorePayOnlineAccountSetup,
} from '../../../shared/hooks/payments';
import { useUpdateBookingConfigurations } from '../../../shared/hooks/booking-configurations';
import { PaymentAvailabilityType } from '../../../shared/types';
import createQueryConfig from '../../../queries';
import { PaymentCaptureTaxIDModal } from './PaymentCaptureTaxIDModal';

const DEFAULT_DOWN_PAYMENTS_PERCENTAGE_MARKS = {
  0: '0%',
  10: '10%',
  20: '20%',
  30: '30%',
  40: '40%',
  50: '50%',
  60: '60%',
  70: '70%',
  80: '80%',
  90: '90%',
  100: '100%',
};
const NOW_IN_SECONDS = Math.floor(Date.now() / 1000);
const TWO_DAYS_IN_SECONDS = 2 * 24 * 60 * 60;

const StyledPaymentSetupPageWrapper = styled.div`
  width: 100%;
  max-width: 650px;
  padding: 0 24px 24px;
`;

const getPaymentMethodNameButtonText = ({ name }: { name: string }) => {
  if (name === 'paypal') return 'PayPal';
  if (name === 'shorepay_online') return 'Shore Pay Online';
  return 'Stripe';
};

const getPaymentMethodStatusButtonVariant = ({
  status,
}: {
  status: string;
}) => {
  if (status === 'disabled' || status === 'active') return 'filled';
  return 'solid';
};

const getPaymentMethodStatusButtonText = ({ status }: { status: string }) => {
  if (status === 'active')
    return t(
      'application-settings.payments.setup-page.section-payment-gateways.status.active'
    );
  if (status === 'disabled')
    return t(
      'application-settings.payments.setup-page.section-payment-gateways.status.disabled'
    );
  if (status === 'pending')
    return t(
      'application-settings.payments.setup-page.section-payment-gateways.status.pending'
    );
  if (status === 'not_setup')
    return t(
      'application-settings.payments.setup-page.section-payment-gateways.status.setup'
    );
  return 'unknown';
};

function SectionPaymentMethod({
  name,
  status,
  company,
  merchantId,
  legacyStripeSetupArticleUrl,
}: {
  name: string;
  status: string;
  company: string;
  merchantId: string;
  legacyStripeSetupArticleUrl: string;
}) {
  const tools = useGlobals() as any;
  const { trackMethod } = tools;
  const [isCaptureTaxIDModalOpen, setIsCaptureTaxIDModalOpen] = useState(false);
  const {
    mutate: mutateInitiatePayPalSetup,
    isLoading: isLoadingInitiatePayPalSetup,
  } = useInitiatePayPalSetup();

  const {
    mutate: mutateInitiateShorePayOnlineAccountSetup,
    isLoading: isLoadingInitiateShorePayOnlineAccountSetup,
  } = useInitiateShorePayOnlineAccountSetup();

  const paymentMethodNameText = getPaymentMethodNameButtonText({ name });
  const paymentMethodStatusText = getPaymentMethodStatusButtonText({ status });
  const paymentMethodStatusVariant = getPaymentMethodStatusButtonVariant({
    status,
  });
  const shouldDisplayWarningInstructions =
    status === 'disabled' || status === 'pending';
  const warningInstructionsText =
    status === 'pending'
      ? t(
          'application-settings.payments.setup-page.section-payment-gateways.instructions.for-pending'
        )
      : t(
          'application-settings.payments.setup-page.section-payment-gateways.instructions.for-inactive'
        );

  const handleOnSetupButtonClick = () => {
    if (name === 'stripe' && status === 'not_setup') {
      trackMethod(
        'application-settings-app_online_payment_settings_screen_setup_stripe_clicked'
      );
      window.open(legacyStripeSetupArticleUrl, '_blank');
    }
    if (name === 'paypal' && status === 'not_setup') {
      trackMethod(
        'application-settings-app_online_payment_settings_screen_setup_paypal_clicked'
      );
      mutateInitiatePayPalSetup({ merchantId });
    }
    if (name === 'shorepay_online' && status === 'not_setup') {
      trackMethod(
        'application-settings-app_online_payment_settings_screen_setup_shore_pay_online_account_clicked'
      );
      mutateInitiateShorePayOnlineAccountSetup(
        {
          company,
          merchantId,
        },
        {
          onError: (error: unknown) => {
            if (error instanceof Error) {
              if (
                error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
              ) {
                setIsCaptureTaxIDModalOpen(true);
              }
            }
          },
        }
      );
    }
  };

  const handleOnPayPalDisableButtonClick = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_disable_paypal_modal_opened'
    );
  };

  const handleOnCaptureTaxIDModalClose = () => {
    setIsCaptureTaxIDModalOpen(false);
  };

  const handleOnCaptureTaxIDModalFormSubmit = ({
    taxId,
  }: {
    taxId: string;
  }) => {
    mutateInitiateShorePayOnlineAccountSetup(
      {
        taxId,
        company,
        merchantId,
      },
      {
        onSuccess: () => setIsCaptureTaxIDModalOpen(false),
      }
    );
  };

  return (
    <Row style={{ marginTop: 20 }} gutter={[30, 16]} align="middle">
      <Col span={6}>
        <Typography.Text strong>{paymentMethodNameText}</Typography.Text>
      </Col>
      <Col span={18}>
        <Space size="middle">
          <Button
            color="primary"
            variant={paymentMethodStatusVariant}
            loading={
              isLoadingInitiatePayPalSetup ||
              isLoadingInitiateShorePayOnlineAccountSetup
            }
            onClick={handleOnSetupButtonClick}
          >
            {paymentMethodStatusText}
          </Button>
          {name === 'paypal' && status === 'active' && (
            <Button
              color="primary"
              variant="filled"
              onClick={handleOnPayPalDisableButtonClick}
            >
              {t(
                'application-settings.payments.setup-page.section-payment-gateways.status.disable'
              )}
            </Button>
          )}

          {isCaptureTaxIDModalOpen && (
            <PaymentCaptureTaxIDModal
              isLoadingShorePayOnlineSetup={
                isLoadingInitiateShorePayOnlineAccountSetup
              }
              OnClickClose={handleOnCaptureTaxIDModalClose}
              onFormSubmit={handleOnCaptureTaxIDModalFormSubmit}
            />
          )}
        </Space>
      </Col>
      {shouldDisplayWarningInstructions && (
        <Col span={24} style={{ maxWidth: 650 }}>
          <Alert showIcon type="warning" message={warningInstructionsText} />
        </Col>
      )}
    </Row>
  );
}

function SectionPaymentGateway({
  company,
  merchantId,
  paymentMethods,
  isPayPalFeatureToggleEnabled,
  legacyStripeSetupArticleUrl,
}: {
  company: string;
  merchantId: string;
  isPayPalFeatureToggleEnabled: boolean;
  legacyStripeSetupArticleUrl: string;
  paymentMethods: any[];
}) {
  const tooltipText = isPayPalFeatureToggleEnabled
    ? t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip'
      )
    : t(
        'application-settings.payments.setup-page.section-payment-gateways.title-tooltip-for-stripe-only'
      );

  return (
    <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {t(
            'application-settings.payments.setup-page.section-payment-gateways.title'
          )}
          <Tooltip title={tooltipText}>
            <QuestionCircleOutlined
              style={{
                fontSize: 18,
                marginLeft: 8,
                color: '#bdc3c7',
              }}
            />
          </Tooltip>
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 650 }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.setup-page.section-payment-gateways.description'
              ),
            }}
          />
        </Typography.Text>
      </Col>
      <Col span={24}>
        {paymentMethods.map((paymentMethod: any) => (
          <SectionPaymentMethod
            key={paymentMethod.name}
            company={company}
            merchantId={merchantId}
            name={paymentMethod.name}
            status={paymentMethod.status}
            legacyStripeSetupArticleUrl={legacyStripeSetupArticleUrl}
          />
        ))}
      </Col>
    </Row>
  );
}

function SectionPaymentAvailability({
  bookingConfigs,
  paymentMethods,
  handleOnDownPaymentsPercentageChange,
  handleOnBookingConfigurationsChange,
}: {
  bookingConfigs: any;
  paymentMethods: any[];
  handleOnDownPaymentsPercentageChange: (percentageValue: number) => void;
  handleOnBookingConfigurationsChange: (param: {
    paymentAvailability: PaymentAvailabilityType;
  }) => void;
}) {
  const shorepayOnlineActivatedAt =
    bookingConfigs?.shorepayOnlineActivatedAt || 0;

  const [form] = Form.useForm<{
    paymentAvailability: PaymentAvailabilityType;
  }>();
  const formValues = Form.useWatch<{
    paymentAvailability: PaymentAvailabilityType;
  }>([], form);

  const isWithinFirstTwoDays =
    NOW_IN_SECONDS < shorepayOnlineActivatedAt + TWO_DAYS_IN_SECONDS;

  const isShorePayOnlinePaymentMethodActive = paymentMethods.some(
    (paymentMethod: any) =>
      paymentMethod.name === 'shorepay_online' &&
      paymentMethod.status === 'active'
  );

  const shouldNotShowOptionalPaymentType =
    isShorePayOnlinePaymentMethodActive && isWithinFirstTwoDays;

  const paymentTypes =
    isShorePayOnlinePaymentMethodActive && shouldNotShowOptionalPaymentType
      ? ['mandatory', 'disabled']
      : ['mandatory', 'optional', 'disabled'];

  const DEFAULT_DOWN_PAYMENTS_PERCENTAGE =
    isShorePayOnlinePaymentMethodActive &&
    bookingConfigs?.customerPayment === 'mandatory'
      ? 100
      : 0;

  const downPaymentSliderPercentage =
    paymentMethods.find((item) => item.name === 'shorepay_online')?.meta
      ?.down_payment_percentage || DEFAULT_DOWN_PAYMENTS_PERCENTAGE;

  const handleOnFormValuesChange = (
    _: any,
    allValues: {
      paymentAvailability: PaymentAvailabilityType;
    }
  ) => {
    const paymentAvailabilityValue = allValues.paymentAvailability;
    handleOnBookingConfigurationsChange({
      paymentAvailability: paymentAvailabilityValue,
    });
  };

  return (
    <Row style={{ marginTop: 20 }} gutter={[16, 36]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {t(
            'application-settings.payments.setup-page.section-booking-payment.title'
          )}
          <Tooltip
            title={
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'application-settings.payments.setup-page.section-booking-payment.title-tooltip'
                  ),
                }}
              />
            }
          >
            <QuestionCircleOutlined
              style={{
                fontSize: 18,
                marginLeft: 8,
                color: '#bdc3c7',
              }}
            />
          </Tooltip>
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 650 }}
        >
          {t(
            'application-settings.payments.setup-page.section-booking-payment.description'
          )}
        </Typography.Text>
      </Col>
      <Col span={24}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            paymentAvailability: bookingConfigs.customerPayment,
          }}
          onValuesChange={handleOnFormValuesChange}
        >
          <Form.Item name="paymentAvailability">
            <Radio.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
            >
              {paymentTypes.map((type) => (
                <>
                  <Radio key={type} value={type} style={{ fontWeight: '400' }}>
                    <Typography.Text>
                      {t(
                        `application-settings.payments.setup-page.section-booking-payment.payment-types.${type === 'disabled' ? 'pay-at-location' : type}`
                      )}
                    </Typography.Text>
                  </Radio>
                  {type === 'optional' &&
                    formValues &&
                    formValues?.paymentAvailability === 'optional' && (
                      <Flex
                        style={{
                          width: '100%',
                          marginTop: 0,
                        }}
                      >
                        <Alert
                          showIcon
                          message={t(
                            `application-settings.payments.setup-page.section-booking-payment.warning.payment-type-optional`
                          )}
                          type="warning"
                        />
                      </Flex>
                    )}
                  {type === 'mandatory' &&
                    isShorePayOnlinePaymentMethodActive && (
                      <Flex
                        justify="center"
                        align="center"
                        vertical
                        style={{ padding: '0 10px' }}
                      >
                        <Slider
                          step={null}
                          marks={DEFAULT_DOWN_PAYMENTS_PERCENTAGE_MARKS}
                          defaultValue={downPaymentSliderPercentage}
                          tooltip={{ open: false }}
                          style={{
                            width: '100%',
                            marginTop: 0,
                            marginBottom: 20,
                          }}
                          disabled={
                            formValues &&
                            formValues?.paymentAvailability !== 'mandatory'
                          }
                          onChange={handleOnDownPaymentsPercentageChange}
                        />
                        {formValues &&
                          formValues?.paymentAvailability === 'mandatory' && (
                            <Row justify="end">
                              <Col span={14}>
                                <Flex
                                  gap={20}
                                  justify="space-between"
                                  style={{ width: '115%' }}
                                >
                                  <Typography.Text style={{ color: '#00d0be' }}>
                                    {t(
                                      `application-settings.payments.setup-page.section-booking-payment.labels.most-popular`
                                    )}
                                  </Typography.Text>
                                  <Typography.Text style={{ color: '#00d0be' }}>
                                    {t(
                                      `application-settings.payments.setup-page.section-booking-payment.labels.full-protection`
                                    )}
                                  </Typography.Text>
                                </Flex>
                              </Col>
                              <Col span={24}>
                                <Flex
                                  style={{
                                    width: '100%',
                                    marginTop: 10,
                                  }}
                                >
                                  <Typography.Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                  >
                                    {t(
                                      `application-settings.payments.setup-page.section-booking-payment.note.payment-type-mandatory`
                                    )}
                                  </Typography.Text>
                                </Flex>
                              </Col>
                            </Row>
                          )}
                      </Flex>
                    )}
                </>
              ))}
            </Radio.Group>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

function SectionPaymentTransactions() {
  const navigate = useNavigate();
  const tools = useGlobals() as any;
  const { trackMethod } = tools;

  const handleOnTransactionsButtonClick = () => {
    trackMethod(
      'application-settings-app_online_payment_settings_screen_transactions_clicked'
    );
    navigate('/transactions');
  };

  return (
    <Row style={{ marginTop: 20 }} gutter={[16, 36]}>
      <Col span={24}>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {t(
            'application-settings.payments.setup-page.section-transactions.title'
          )}
        </Typography.Title>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', maxWidth: 650 }}
        >
          {t(
            'application-settings.payments.setup-page.section-transactions.description'
          )}
        </Typography.Text>
      </Col>
      <Col span={24}>
        <Button
          type="primary"
          size="large"
          onClick={handleOnTransactionsButtonClick}
        >
          {t(
            'application-settings.payments.setup-page.section-transactions.button-transactions'
          )}
        </Button>
      </Col>
    </Row>
  );
}

export function PaymentSetupPage() {
  const { queryClient, apiClient } = useGlobals() as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { merchantSettings } = useMerchantSettings();
  const { merchantId, company, featureToggles } = merchantSettings || {};

  const bookingConfigs = useSelector((state: any) => state.bookingConfigs);
  const currentMerchant = useSelector((state: any) => state.currentMerchant);
  const helpCenterArticles =
    useSelector(
      (state: any) => state.whiteLabel.config.schemaless.helpcenter
    ) || {};

  const { mutate: mutateUpdateBookingConfigurations } =
    useUpdateBookingConfigurations();

  const { mutate: mutateSetupDownPayments } = useMutation(
    queryConfig.downPayments.setupDownPayments({ merchantId })
  );

  const { mutate: mutateDisableDownPayments } = useMutation(
    queryConfig.downPayments.disableDownPayments({ merchantId })
  );

  const paymentMethods = currentMerchant.paymentSettings?.paymentMethods ?? [];
  const legacyStripeSetupArticleUrl = helpCenterArticles?.onlinePayment || '';

  const isPayPalFeatureToggleEnabled =
    featureToggles?.paypal_payment_enabled || false;

  const handleOnFormBookingConfigurationsChange = (param: {
    paymentAvailability: PaymentAvailabilityType;
  }) => {
    mutateUpdateBookingConfigurations({
      merchantId,
      paymentAvailability: param.paymentAvailability,
    });
    if (param.paymentAvailability === 'mandatory') {
      mutateSetupDownPayments({ percentage: 100 } as any);
    }
  };

  const handleOnDownPaymentsPercentageChange = debounce((value: number) => {
    if (value > 0) {
      mutateSetupDownPayments({ percentage: value } as any);
    } else {
      mutateDisableDownPayments();
      handleOnFormBookingConfigurationsChange({
        paymentAvailability: 'disabled',
      });
    }
  }, 300);

  return (
    <StyledPaymentSetupPageWrapper>
      <SectionPaymentGateway
        company={company}
        merchantId={merchantId}
        paymentMethods={paymentMethods}
        legacyStripeSetupArticleUrl={legacyStripeSetupArticleUrl}
        isPayPalFeatureToggleEnabled={isPayPalFeatureToggleEnabled}
      />
      <Divider />
      <SectionPaymentAvailability
        bookingConfigs={bookingConfigs}
        paymentMethods={paymentMethods}
        handleOnDownPaymentsPercentageChange={
          handleOnDownPaymentsPercentageChange
        }
        handleOnBookingConfigurationsChange={
          handleOnFormBookingConfigurationsChange
        }
      />
      <Divider />
      <SectionPaymentTransactions />
    </StyledPaymentSetupPageWrapper>
  );
}
